import React, { useCallback, useEffect, useState } from 'react';
import { FormControlLabel, Switch, TextField } from '@mui/material';
import { GroupsOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { settingsService } from '../../services/settingsService';
import { Group } from '../../types';
import SettingsList from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';
import { ToneChip } from '../common/StatusChip';

/** Every group the school has — a settings list is never paged. */
const ALL = 500;

const GroupsSettings: React.FC = () => {
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const writable = canWrite('settings_groups');

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Group | null>(null);
  const [form, setForm] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await settingsService.getGroups({ limit: ALL });
      setGroups(res.data?.groups ?? []);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load student groups', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const edit = (g: Group) => {
    setEditing(g);
    setForm({ name: g.name, description: g.description ?? '', isActive: g.isActive });
  };

  const save = async () => {
    if (!editing || !form.name.trim()) return;
    setSaving(true);
    try {
      await settingsService.updateGroup(editing._id, { ...form, name: form.name.trim() });
      enqueueSnackbar('Group saved', { variant: 'success' });
      setEditing(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save this group', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsList
        rows={groups}
        loading={loading}
        rowKey={g => g._id}
        name={g => g.name}
        subtitle={g => g.description || undefined}
        muted={g => !g.isActive}
        meta={g => (g.isActive ? null : <ToneChip label="Inactive" tone="neutral" />)}
        searchText={g => `${g.name} ${g.description ?? ''}`}
        canWrite={writable}
        onEdit={edit}
        remove={{
          run: g => settingsService.deleteGroup(g._id),
          noun: 'group',
          consequence: () => 'The group will be removed. A group that students are still placed in cannot be deleted — make it inactive instead.',
        }}
        quickAdd={{
          noun: { one: 'group', many: 'groups' },
          placeholder: 'Add groups, e.g. Pre-Engineering, Pre-Medical, Computer Science',
          create: name => settingsService.createGroup({ name }),
          presets: [
            { label: 'Science streams', names: ['Pre-Engineering', 'Pre-Medical', 'Computer Science'] },
            { label: 'Matric groups', names: ['Science', 'Biology', 'Computer', 'Arts'] },
          ],
        }}
        reload={load}
        empty={{
          icon: <GroupsOutlined />,
          title: 'No student groups yet',
          description: 'Groups are the streams students are placed in. Add them above — several at once if you like.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title="Edit group"
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        saveDisabled={!form.name.trim()}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          error={!form.name.trim()}
          helperText={!form.name.trim() ? 'A group needs a name' : ' '}
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Optional"
          multiline
          minRows={2}
          fullWidth
        />
        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
          label="Active — offered when placing students"
        />
      </SettingsDialog>
    </>
  );
};

export default GroupsSettings;
